import type {
  AddCorrelationsBulkRequestData,
  CorrelationsRequest,
  CorrelationsResponse,
  UpdateCorrelationsRequestData
} from '@library/types/correlations';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';

const { admin } = apiPaths;
const correlationsPath = `${admin}correlation_coefficients`;

export const correlationsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCorrelations: build.query<
      CorrelationsResponse | undefined,
      CorrelationsRequest
    >({
      query: ({
        page,
        pageSize,
        statType1,
        statType2,
        firstPosition,
        secondPosition,
        firstChoice,
        secondChoice,
        isSameTeam
      }) => ({
        url: correlationsPath,
        method: 'GET',
        params: {
          page,
          page_size: pageSize,
          stat_type1: statType1,
          stat_type2: statType2,
          first_position: firstPosition,
          second_position: secondPosition,
          is_same_team: isSameTeam,
          first_choice: firstChoice,
          second_choice: secondChoice
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      keepUnusedDataFor: 0,
      providesTags: ['Correlations']
    }),
    updateCorrelations: build.mutation<void, UpdateCorrelationsRequestData>({
      query: ({
        approved_by,
        correlation_id,
        first_choice,
        first_position,
        first_stat_type,
        id,
        is_same_team,
        last_modified_user_name,
        second_choice,
        second_position,
        second_stat_type,
        probability,
        requested_by
      }) => ({
        url: `${correlationsPath}/${id}`,
        method: 'PATCH',
        body: {
          approved_by,
          correlation_id,
          first_choice,
          first_position,
          first_stat_type,
          is_same_team,
          last_modified_user_name,
          second_choice,
          second_position,
          second_stat_type,
          probability,
          requested_by
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformErrorResponse: (response) => response.data
    }),
    createCorrelations: build.mutation<void, AddCorrelationsBulkRequestData>({
      query: (correlationsData) => ({
        url: correlationsPath,
        method: 'POST',
        body: correlationsData,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformErrorResponse: (response) => response.data
    }),
    createCorrelationsCsv: build.mutation<CorrelationsResponse, FormData>({
      query: (formData) => ({
        url: correlationsPath,
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'multipart/form-data'
        },
        validateStatus: (response, result: CorrelationsResponse) =>
          response && result.success === true
      }),
      transformErrorResponse: (response) => response.data
    }),
    deleteCorrelations: build.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `${correlationsPath}/${id}`,
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformErrorResponse: (response) => response.data
    })
  })
});

export const {
  useCreateCorrelationsMutation,
  useCreateCorrelationsCsvMutation,
  useDeleteCorrelationsMutation,
  useGetCorrelationsQuery,
  useUpdateCorrelationsMutation
} = correlationsApi;
